import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { getUtilityTemplateCategories } from '@/services/api/utility-template-categories';
import { setGroupUtilityCategories } from '@/services/api/utility-template-categories';
import type { UtilityTemplateGroup } from '@/services/api/utility-templates';

interface GroupCategoriesSelectProps {
    group: UtilityTemplateGroup;
}

/**
 * Multi-select de categorias/pasta no card do grupo. Cada mudança chama o PUT
 * de substituição total (`utility_category_ids`) — não há PATCH incremental
 * no backend, então a mutation sempre envia a lista completa resultante.
 */
export default function GroupCategoriesSelect({ group }: GroupCategoriesSelectProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: categories = [] } = useQuery({
        queryKey: ['utility-template-categories'],
        queryFn: getUtilityTemplateCategories,
        staleTime: 1000 * 30,
    });

    const { mutate: updateCategories, isPending } = useMutation({
        mutationFn: (categoryIds: number[]) => setGroupUtilityCategories(group.id, categoryIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['utility-template-groups'] });
        },
        onError: () => {
            toast({ title: 'Erro ao atualizar categorias do template.', variant: 'destructive' });
        },
    });

    const currentValues = (group.utility_category_ids ?? []).map(String);

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <SearchableMultiSelect
                values={currentValues}
                onValuesChange={(vals) => updateCategories(vals.map(Number))}
                options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                placeholder="Sem categoria"
                searchPlaceholder="Buscar categoria..."
                showTags={false}
                disabled={isPending}
                selectedLabel={(n) => `${n} categoria${n > 1 ? 's' : ''}`}
                className="h-8 text-xs"
            />
        </div>
    );
}
